import type { Dictionary, ExplainMessages } from '../models'
// @ts-strict-ignore
import {
  EqualizeCriteriaTypeEnum,
  ExplainTotalTypeEnum,
  QueryRankingCategoryTypeEnum,
  RootScoreCriteriaTypeEnum,
  SemanticSearchTypeEnum,
  WisdomOfTheCrowdTypeEnum,
} from '@empathybroker/tsclient-play/dist/enums/search/debug/result/score-criteria-detail-type.enum'

import { ExplainSectionEnum, LocaleEnum } from '../enums'

const criteriaTitle = (type: string): string =>
  ({
    [RootScoreCriteriaTypeEnum.EQUALIZE]: 'Equalize',
    [RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH]: 'Semantic',
    [RootScoreCriteriaTypeEnum.QUERY_RANKING]: 'Results Ranking',
    [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: 'Wisdom of the Crowd',
    [RootScoreCriteriaTypeEnum.PERSONALIZATION]: 'Customization',
    [RootScoreCriteriaTypeEnum.TERM_POSITION]: 'Term position',
    [EqualizeCriteriaTypeEnum.TEXTUAL_MATCH]: 'Textual Match',
    [EqualizeCriteriaTypeEnum.GLOBAL_BUSINESS_RULES]: 'Global Business Rules',
    [EqualizeCriteriaTypeEnum.FUNCTION_SCORE]: 'Function Score',
    [QueryRankingCategoryTypeEnum.PRODUCTS]: 'Products',
    [QueryRankingCategoryTypeEnum.ATTRIBUTES]: 'Attributes',
    [WisdomOfTheCrowdTypeEnum.TOP_PRODUCT]: 'Top Product',
    [WisdomOfTheCrowdTypeEnum.TOP_ATTRIBUTES]: 'Top Attributes',
    [SemanticSearchTypeEnum.SDI]: 'Relevance',
    [ExplainTotalTypeEnum.TOTAL]: 'Total',
  })[type]

const EXPLAIN_MESSAGES_EN: ExplainMessages = {
  search: {
    placeholder: 'Type a query',
    selectFiltersPlaceholder: 'Select filters to start searching',
    mode: {
      search: {
        title: 'Keyword',
        tooltip:
          'Matches the exact keywords in the query with the indexed terms in your product catalogue.',
      },
      semantics: {
        title: 'Semantics',
        tooltip:
          'Uses machine learning models to understand the meaning and context behind the query.',
      },
      hybrid: {
        title: 'Hybrid',
        tooltip:
          'Combines keyword and semantic-based searches to return more accurate and relevant results.',
      },
    },
  },
  filters: {
    title: 'Select filters',
    accept: 'Apply filters',
    endpointId: {
      label: 'Endpoint',
    },
    browseField: {
      label: 'Attribute',
    },
    browseValue: {
      label: 'Attribute value',
    },
  },
  exportability: {
    title: 'Download the top products for your query results',
    tooltip: 'Download the top products for your query results',
    titleBrowse: 'Download the top products for your attribute and attribute value results',
    tooltipBrowse: 'Download the top products for your attribute and attribute value results',
    download: 'download',
    downloadSucceed: {
      title: 'Congratulations! Your download has completed successfully',
      message:
        'You should now be able to access your file from your browser. ' +
        'If you have any questions or concerns, please reach out for assistance. Thank You!',
    },
    downloadOptions: {
      confidentialData: 'Confidential data',
    },
    csvColumns: {
      position: 'Position',
      product: 'Product',
      description: 'Description',
      brand: 'Brand',
      cfSize: 'Customer Facing Size',
      cfDepartment: 'Customer Facing Department',
      cfCommodity: 'Customer Facing Commodity',
      cfSubCommodity: 'Customer Facing Sub-Commodity',
      primaryDepartment: 'Primary Department',
      recapDepartment: 'Recap Department',
      ftDepartment: 'Family Tree Department',
      ftCommodity: 'Family Tree Commodity',
      ftSubCommodity: 'Family Tree Sub-Commodity',
      totalScore: 'Total Score',
      textualMatch: 'Textual Match',
      semanticSearchInfo: 'Relevance',
      semanticMatch: 'Semantic Match',
      functionScore: 'Function Score',
      sponsoredScore: 'Monetization',
      gbrBoost: 'Boosts (Global business rules)',
      gbrBuries: 'Buries (Global business rules)',
      productBoost: 'Product Boosts (Results Ranking)',
      productBury: 'Product Buries (Results Ranking)',
      attributeBoost: 'Attribute Boosts (Results Ranking)',
      attributeBury: 'Attribute Buries (Results Ranking)',
      topProduct: 'Top Product (Wisdom of the Crowd)',
      topAttributes: 'Top Attributes (Wisdom of the Crowd)',
      termPosition: 'Term Position',
      queryText: 'Query',
      queryTextBrowse: 'Browse field and value',
      dateTime: 'Date Time',
      userId: 'User ID',
      numberOfProducts: 'Number of products',
      customization: 'Customization',
      tieBreaker: 'Tie Breaker',
    },
  },
  results: '{counter} results',
  clearFilters: 'Clear filters',
  noDataFound: 'No results found',
  topResults: 'Top {count} products',
  facets: {
    title: 'Facets',
    searchPlaceholder: 'Search',
    showMore: 'Show more ({difference})',
    showLess: 'Show less ({difference})',
  },
  clearSelection: 'Clear selection ({count})',
  score: {
    label: 'Score:',
    criterias: {
      [RootScoreCriteriaTypeEnum.EQUALIZE]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.EQUALIZE),
        description:
          'Equalize: String matching that shows how the product scores vary depending on various global settings',
      },
      [RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH),
        description:
          'Semantic: Understanding of the meaning and scoring of words based on their relationship and relevancy',
      },
      [RootScoreCriteriaTypeEnum.QUERY_RANKING]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.QUERY_RANKING),
        description:
          "Results Ranking: Shows how the product's score changes when queries are configured to be boosted or buried",
      },
      [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD),
        description:
          "Wisdom of the crowd: Shows how the product's score changes depending on its popularity among the users",
      },
      [RootScoreCriteriaTypeEnum.TERM_POSITION]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.TERM_POSITION),
        description:
          "Term position: Shows how the product's score changes depending on the order of the words in the query",
      },
      [RootScoreCriteriaTypeEnum.PERSONALIZATION]: {
        label: 'Customization',
        description:
          "Customization: Shows how the product's score changes depending on the household behavior (top products picked may increase their score)",
      },
      [ExplainTotalTypeEnum.TOTAL]: {
        label: criteriaTitle(ExplainTotalTypeEnum.TOTAL),
      },
      [EqualizeCriteriaTypeEnum.TEXTUAL_MATCH]: {
        label: criteriaTitle(EqualizeCriteriaTypeEnum.TEXTUAL_MATCH),
      },
      semanticMatch: {
        label: 'Semantic Match',
      },
      [EqualizeCriteriaTypeEnum.GLOBAL_BUSINESS_RULES]: {
        label: criteriaTitle(EqualizeCriteriaTypeEnum.GLOBAL_BUSINESS_RULES),
      },
      [EqualizeCriteriaTypeEnum.FUNCTION_SCORE]: {
        label: criteriaTitle(EqualizeCriteriaTypeEnum.FUNCTION_SCORE),
      },
      sponsoredScore: {
        label: 'Monetization',
      },
      [QueryRankingCategoryTypeEnum.PRODUCTS]: {
        label: criteriaTitle(QueryRankingCategoryTypeEnum.PRODUCTS),
      },
      [QueryRankingCategoryTypeEnum.ATTRIBUTES]: {
        label: criteriaTitle(QueryRankingCategoryTypeEnum.ATTRIBUTES),
      },
      [WisdomOfTheCrowdTypeEnum.TOP_PRODUCT]: {
        label: criteriaTitle(WisdomOfTheCrowdTypeEnum.TOP_PRODUCT),
      },
      [WisdomOfTheCrowdTypeEnum.TOP_ATTRIBUTES]: {
        label: criteriaTitle(WisdomOfTheCrowdTypeEnum.TOP_ATTRIBUTES),
      },
      [SemanticSearchTypeEnum.SDI]: {
        label: criteriaTitle(SemanticSearchTypeEnum.SDI),
      },
    },
    max: 'Max',
    total: 'Total',
  },
  carousel: {
    [RootScoreCriteriaTypeEnum.EQUALIZE]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.EQUALIZE),
      description:
        'The score for each product is first calculated depending on the query match and the values set in Equalize.',
    },
    [RootScoreCriteriaTypeEnum.QUERY_RANKING]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.QUERY_RANKING),
      description:
        'Then the points from the different results rankings configured are added to the product score.',
    },
    [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD),
      description:
        'Next, shopper behavior patterns are considered. Collective shopper interactions with the same product ' +
        'make the product score increase.',
    },
    [RootScoreCriteriaTypeEnum.PERSONALIZATION]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.PERSONALIZATION),
      description: 'Also, the points obtained from any custom scoring rule are added to the score.',
    },
    [RootScoreCriteriaTypeEnum.TERM_POSITION]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.TERM_POSITION),
      description:
        'Finally, the position of search terms in the query is taken into account to give the product’s overall score.',
    },
  },
  tiebreakers: {
    label: 'Tiebreakers',
    description:
      'Shows the criteria by which one product will be positioned higher than another when they have the same score',
  },
  tableRows: {
    queryRanking: {
      product: 'Product boost / bury / pin',
    },
  },
  optimize: {
    label: 'Optimize',
  },
  aside: {
    view: 'View Selection',
    collapse: 'Collapse Selection',
  },
  pinned: {
    label: 'Pinned (no score)',
  },
  semantics: {
    info: 'This query doesn’t offer results. Yet, your store is currently showing product  <br /> suggestions carousels thanks to <b> semantics </b> <span class="eicon" style="font-weight: 600; font-size: 20px">highlights</span>',
    noDataFound: 'No results found',
  },
  sections: {
    [ExplainSectionEnum.SEARCH]: {
      label: 'Search',
      browseLabel: 'Browse',
    },
    [ExplainSectionEnum.QUERY_ANALYSIS]: {
      label: 'Query Analysis',
    },
    [ExplainSectionEnum.PRODUCT_ANALYSIS]: {
      label: 'Product Analysis',
    },
  },
  queryAnalysis: {
    sections: {
      searchMetrics: {
        label: 'Search Metrics',
        tabs: {
          evolution: 'Evolution Over Time',
          funnel: 'Funnel',
          distribution: 'Query Distribution',
        },
      },
      searchData: {
        label: 'Search Data',
        title: 'Top 10 products',
        noResults: {
          title: 'Oops!',
          description: 'There is no search data for this query.',
        },
      },
      configurations: {
        label: 'Configurations',
      },
    },
    downloadPdf: {
      buttonTooltip: 'Download the current view as a PDF file',
      details: {
        query: 'Query',
        user: 'User',
      },
    },
  },
  productAnalysis: {
    position: 'Position',
    totalScore: 'Score',
    noScore: 'No Score',
    pinnedProductTooltip: 'Pinned product',
  },
  result: {
    productAnalysisButton: 'Product Analysis',
  },
  header: {
    metricsTooltip: '{kpi} last 7 days. Click to see more details',
  },
  resultInfo: {
    tooltip: 'Details about how the product is indexed',
  },
  compare: {
    buttonTooltip: 'Compare the same product in another query',
    title: 'Query Comparison',
    noResults: {
      paragraph1:
        "The query you have searched for doesn't include the product in the first 1250 results.",
      paragraph2: 'Please, enter a new query.',
    },
  },
}

const EXPLAIN_MESSAGES_ES: ExplainMessages = {
  search: {
    placeholder: 'Escribe una query',
    selectFiltersPlaceholder: 'Select filters to start searching',
    mode: {
      search: {
        title: '',
        tooltip: '',
      },
      semantics: {
        title: '',
        tooltip: '',
      },
      hybrid: {
        title: '',
        tooltip: '',
      },
    },
  },
  filters: {
    title: 'Selecciona filtros',
    accept: 'Aplicar filtros',
    endpointId: {
      label: 'Endpoint',
    },
    browseField: {
      label: 'Atributo',
    },
    browseValue: {
      label: 'Valor del atributo',
    },
  },
  exportability: {
    title: '',
    tooltip: '',
    titleBrowse: '',
    tooltipBrowse: '',
    download: '',
    downloadSucceed: {
      title: '',
      message: '',
    },
    downloadOptions: {
      confidentialData: '',
    },
    csvColumns: {
      position: '',
      product: '',
      description: '',
      brand: '',
      cfSize: '',
      cfDepartment: '',
      cfCommodity: '',
      cfSubCommodity: '',
      primaryDepartment: '',
      recapDepartment: '',
      ftDepartment: '',
      ftCommodity: '',
      ftSubCommodity: '',
      totalScore: '',
      textualMatch: '',
      semanticSearchInfo: '',
      semanticMatch: '',
      functionScore: '',
      sponsoredScore: '',
      gbrBoost: '',
      gbrBuries: '',
      productBoost: '',
      productBury: '',
      attributeBoost: '',
      attributeBury: '',
      topProduct: '',
      topAttributes: '',
      termPosition: '',
      queryText: '',
      queryTextBrowse: '',
      dateTime: '',
      userId: '',
      numberOfProducts: '',
      customization: '',
      tieBreaker: '',
    },
  },
  results: '{counter} resultados',
  clearFilters: '',
  noDataFound: 'No se han encontrado resultados',
  topResults: 'Primeros {count} productos',
  facets: {
    title: 'Facets',
    searchPlaceholder: 'Buscar',
    showMore: 'Mostrar más ({difference})',
    showLess: 'Mostrar menos ({difference})',
  },
  clearSelection: 'Limpiar selección ({count})',
  score: {
    label: 'Puntuación:',
    criterias: {
      [RootScoreCriteriaTypeEnum.EQUALIZE]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.EQUALIZE),
        description:
          'Muestra como varía la puntuación del producto dependiendo del valor del Equalize y de la coincidencia de la búsqueda',
      },
      [RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH),
      },
      [RootScoreCriteriaTypeEnum.QUERY_RANKING]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.QUERY_RANKING),
        description:
          'Muestra como la puntuación del producto varía cuando las búsquedas se configuran con boost y bury',
      },
      [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD),
        description:
          'Muestra como varía la puntuación del producto dependiendo de la popularidad del mismo',
      },
      [RootScoreCriteriaTypeEnum.TERM_POSITION]: {
        label: criteriaTitle(RootScoreCriteriaTypeEnum.TERM_POSITION),
        description:
          'Cambia la puntuación del producto en función del orden de las palabras en la búsqueda',
      },
      [RootScoreCriteriaTypeEnum.PERSONALIZATION]: {
        label: 'Personalization',
        description:
          'Cambia la puntuación del producto en función del comportamiento de los usuarios de una unidad familiar: ' +
          'sus productos más escogidos aumentan su puntuación',
      },
      [ExplainTotalTypeEnum.TOTAL]: {
        label: criteriaTitle(ExplainTotalTypeEnum.TOTAL),
      },
      [EqualizeCriteriaTypeEnum.TEXTUAL_MATCH]: {
        label: criteriaTitle(EqualizeCriteriaTypeEnum.TEXTUAL_MATCH),
      },
      semanticMatch: {
        label: 'Semantic Match',
      },
      [EqualizeCriteriaTypeEnum.GLOBAL_BUSINESS_RULES]: {
        label: criteriaTitle(EqualizeCriteriaTypeEnum.GLOBAL_BUSINESS_RULES),
      },
      [EqualizeCriteriaTypeEnum.FUNCTION_SCORE]: {
        label: criteriaTitle(EqualizeCriteriaTypeEnum.FUNCTION_SCORE),
      },
      sponsoredScore: {
        label: 'Monetization',
      },
      [QueryRankingCategoryTypeEnum.PRODUCTS]: {
        label: criteriaTitle(QueryRankingCategoryTypeEnum.PRODUCTS),
      },
      [QueryRankingCategoryTypeEnum.ATTRIBUTES]: {
        label: criteriaTitle(QueryRankingCategoryTypeEnum.ATTRIBUTES),
      },
      [WisdomOfTheCrowdTypeEnum.TOP_PRODUCT]: {
        label: criteriaTitle(WisdomOfTheCrowdTypeEnum.TOP_PRODUCT),
      },
      [WisdomOfTheCrowdTypeEnum.TOP_ATTRIBUTES]: {
        label: criteriaTitle(WisdomOfTheCrowdTypeEnum.TOP_ATTRIBUTES),
      },
      [SemanticSearchTypeEnum.SDI]: {
        label: criteriaTitle(SemanticSearchTypeEnum.SDI),
      },
    },
    max: 'Máx.',
    total: 'Total',
  },
  carousel: {
    [RootScoreCriteriaTypeEnum.EQUALIZE]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.EQUALIZE),
      description:
        'La puntuación total de cada producto se calcula primero en base al grado de coincidencias de la query ' +
        'y de los valores establecidos en Equalize.',
    },
    [RootScoreCriteriaTypeEnum.QUERY_RANKING]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.QUERY_RANKING),
      description:
        'Después, se suman los puntos de las diferentes reglas de negocio configuradas a la puntuación de los productos.',
    },
    [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD),
      description:
        'A continuación, se tienen en cuenta los patrones de comportamiento de los compradores. ' +
        'Las interacciones de los compradores con un mismo producto hacen que se incremente su puntuación.',
    },
    [RootScoreCriteriaTypeEnum.PERSONALIZATION]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.PERSONALIZATION),
      description:
        'Por último, se añaden los puntos obtenidos de cualquier regla personalizada para obtener ' +
        'la puntuación total del producto.',
    },
    [RootScoreCriteriaTypeEnum.TERM_POSITION]: {
      title: criteriaTitle(RootScoreCriteriaTypeEnum.TERM_POSITION),
      description: '',
    },
  },
  tiebreakers: {
    label: 'Tiebreakers',
    description:
      'Muestra los criterios por los cuales un producto se posicionará más alto que otro cuando tienen la misma puntuación',
  },
  tableRows: {
    queryRanking: {
      product: '',
    },
  },
  optimize: {
    label: 'Optimizar',
  },
  aside: {
    view: 'Ver Selección',
    collapse: 'Colapsar Selección',
  },
  pinned: {
    label: '',
  },
  semantics: {
    info: '',
    noDataFound: '',
  },
  sections: {
    [ExplainSectionEnum.SEARCH]: {
      label: 'Search',
      browseLabel: 'Browse',
    },
    [ExplainSectionEnum.QUERY_ANALYSIS]: {
      label: 'Query Analysis',
    },
    [ExplainSectionEnum.PRODUCT_ANALYSIS]: {
      label: 'Product Analysis',
    },
  },
  queryAnalysis: {
    sections: {
      searchMetrics: {
        label: '',
        tabs: {
          evolution: '',
          funnel: '',
          distribution: '',
        },
      },
      searchData: {
        label: '',
        title: '',
        noResults: {
          title: '',
          description: '',
        },
      },
      configurations: {
        label: '',
      },
    },
    downloadPdf: {
      buttonTooltip: '',
      details: {
        query: '',
        user: '',
      },
    },
  },
  productAnalysis: {
    position: '',
    totalScore: '',
    noScore: '',
    pinnedProductTooltip: 'Pinned product',
  },
  result: {
    productAnalysisButton: '',
  },
  header: {
    metricsTooltip: '',
  },
  resultInfo: {
    tooltip: '',
  },
  compare: {
    buttonTooltip: '',
    title: '',
    noResults: {
      paragraph1: '',
      paragraph2: '',
    },
  },
}

export const EXPLAIN_MESSAGES: Dictionary<{ explain: ExplainMessages }> = {
  [LocaleEnum.ENGLISH]: {
    explain: EXPLAIN_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    explain: EXPLAIN_MESSAGES_ES,
  },
}
